import { IMMUTABLE_GAME_ID } from "@/constants";
import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { notifyError } from "@/utils/monitoring";
import {
  authorizedDelete,
  authorizedFetcherGet,
  authorizedPost,
} from "@/utils/request";
import { useSessionActivity } from "@/utils/sessionActivity";
import { usePrevious } from "@biom3/react";
import { useCallback, useEffect, useState } from "react";
import useSWR, { mutate as mutateSWR } from "swr";
import { useGetWalletGems, useGetWalletLatestEarnings } from "./api";
import { useAccessToken } from "./useAccessToken";

type WishlistStatusResponse = {
  game_id: string;
  updated_at: string;
  created_at: string;
  subscribed: boolean;
};

type pendingWishlistAction = "add" | "remove";
export type WishlistState = "wishlisted" | "notWishlisted" | "pending";
export type GemsState =
  | "alreadyClaimed"
  | "claimed"
  | "unclaimed"
  | "pending"
  | "creatingWallet"
  | "disabled";
const API_PATH = "/v1/games/wishlist";

export const useWishlistHandler = (
  gameId: string,
  gemAmount?: number,
  throwError?: boolean,
) => {
  const [isWishlisting, setIsWishlisting] = useState(false);
  const { passportState, walletAddress, isLoggedIn } = usePassportProvider();
  const previousPassportState = usePrevious(passportState);
  const getAccessToken = useAccessToken();
  const { trackSessionActivity } = useSessionActivity(gameId);

  const [gemsState, setGemsState] = useState<GemsState>("disabled");
  const [pendingAction, setPendingAction] = useState<
    pendingWishlistAction | undefined
  >(undefined);
  const { latestEarnings } = useGetWalletLatestEarnings(walletAddress);

  const { walletGems, mutate } = useGetWalletGems(walletAddress || null, false);

  const checkGemsEligibility = useCallback(
    async (triggeredByFollow: boolean) => {
      if (!walletAddress) {
        return;
      }
      if (!latestEarnings) {
        return;
      }
      try {
        for (let i = 0; i < latestEarnings.earnings.length; i++) {
          if (latestEarnings.earnings[i].ruleId === `${gameId}-wishlist`) {
            // If checking eligibility after following a game, the user just logged in.
            // Therefore we need to show the already claimed message as they saw the gems advertised.
            setGemsState(triggeredByFollow ? "alreadyClaimed" : "disabled");
            return;
          }
        }
        setGemsState("unclaimed");
        return;
      } catch (e) {
        notifyError("Error getting gems earnings", "wishlist");
        return;
      }
    },
    [walletAddress, gameId, latestEarnings],
  );

  // Fix for when you visit a games page directly and the earnings have not been fetched yet
  useEffect(() => {
    checkGemsEligibility(false);
  }, [checkGemsEligibility]);

  const { data: wishlistStatus, mutate: mutateWishlistStatus } = useSWR(
    isLoggedIn && gameId && gameId !== IMMUTABLE_GAME_ID
      ? `/v1/games/user/wishlist?game_id=${gameId}`
      : null,
    (path: string) =>
      authorizedFetcherGet(
        path,
        getAccessToken,
      ) as Promise<WishlistStatusResponse>,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      errorRetryCount: 0,
      shouldRetryOnError: false,
    },
  );
  // Attempt to track session activity if the user is revisiting the games page
  // after having followed it.
  useEffect(() => {
    if (
      !wishlistStatus ||
      !wishlistStatus.subscribed // Check if the user is subscribed (has wishlisted)
    ) {
      return;
    }

    // If the user is subscribed and other prerequisites are met, track activity.
    trackSessionActivity();
  }, [wishlistStatus, trackSessionActivity]);

  const { openLoginModal } = useLoginModal();

  const addToWishlist = useCallback(async () => {
    if (!passportState.authenticated) {
      setPendingAction("add");
      openLoginModal();
      return;
    }

    if (passportState.authenticated && !passportState.zkEvmRegistered) {
      setGemsState("creatingWallet");
      setPendingAction("add");
      return;
    }

    const accessToken = await getAccessToken();
    if (accessToken === undefined) {
      notifyError("Unauthorized post", "wishlist");
      return;
    }
    try {
      setIsWishlisting(true);
      await authorizedPost(API_PATH, { game_id: gameId }, accessToken);

      // invalidate quest completions
      mutateSWR(`questCompletions${gameId}`);
      mutateWishlistStatus(
        {
          game_id: gameId,
          subscribed: true,
          updated_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        },
        false,
      );

      // only optimistic update if the user has claimed gem in this follow action
      if (
        walletGems &&
        (gemsState === "unclaimed" || gemsState === "creatingWallet")
      ) {
        // optimistic update to the gem count and do not refetch as backend state might lag
        // default to 1 unless override is provided
        const newGemBalance = walletGems.gems + (gemAmount ?? 0);
        mutate(
          {
            ...walletGems,
            gems: newGemBalance,
          },
          {
            revalidate: true,
          },
        );
      }

      setGemsState((prev) => {
        return prev === "creatingWallet" || prev === "unclaimed"
          ? "claimed"
          : prev;
      });

      // Track session activity whenever the user follows a game
      trackSessionActivity();
    } catch (e) {
      notifyError("Failed to add game to wishlist", "wishlist");
      if (throwError) throw e;
    } finally {
      setIsWishlisting(false);
    }
  }, [
    passportState,
    mutate,
    walletGems,
    openLoginModal,
    getAccessToken,
    gameId,
    gemsState,
    gemAmount,
    trackSessionActivity,
    mutateWishlistStatus,
    throwError,
  ]);

  const removeFromWishlist = useCallback(async () => {
    // Practically this should never happen, but just in case
    if (!passportState.authenticated) {
      setPendingAction("remove");
      openLoginModal();
      return;
    }

    const accessToken = await getAccessToken();
    if (accessToken === undefined) {
      notifyError("Unauthorized delete", "wishlist");
      return;
    }
    try {
      await authorizedDelete(API_PATH, accessToken, { game_id: gameId });

      mutateWishlistStatus(
        {
          game_id: gameId,
          subscribed: false,
          updated_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        },
        false,
      );

      setGemsState((prev) =>
        prev === "claimed" || prev === "alreadyClaimed" ? "disabled" : prev,
      );
    } catch (e) {
      notifyError("Failed to remove game from wishlist", "wishlist");
    }
  }, [
    passportState,
    openLoginModal,
    getAccessToken,
    gameId,
    mutateWishlistStatus,
  ]);

  useEffect(() => {
    // Wait for authenticated state to be resolved before showing gems reward
    if (
      passportState.ready &&
      passportState.ready !== previousPassportState?.ready
    ) {
      if (!passportState.authenticated) {
        setGemsState("unclaimed");
      } else if (
        !passportState.starkExRegistered &&
        !passportState.zkEvmRegistered
      ) {
        setGemsState("unclaimed");
      } else {
        checkGemsEligibility(false);
      }
    }
  }, [passportState, previousPassportState, checkGemsEligibility]);

  // If already claimed in another session, no secondary text in button
  // If user unfollows, button will have no secondary text
  useEffect(() => {
    if (
      passportState.authenticated &&
      previousPassportState?.authenticated === false
    ) {
      if (pendingAction) {
        // If walletAddress is undefined its a new registration. A wallet address is required for gems to be rewarded.
        if (!walletAddress) {
          setGemsState("creatingWallet");
          return;
        }
        if (pendingAction === "add") {
          const add = async () => {
            // Check eligibility first to see whether to show "already claimed"
            await checkGemsEligibility(true);
            addToWishlist();
          };
          add();
        } else {
          removeFromWishlist();
        }
      }
      setPendingAction(undefined);
    }
  }, [
    passportState,
    previousPassportState,
    pendingAction,
    walletAddress,
    addToWishlist,
    removeFromWishlist,
    checkGemsEligibility,
  ]);

  // Add game to wishlist after user registers their wallet
  useEffect(() => {
    if (
      passportState.zkEvmRegistered &&
      !previousPassportState?.zkEvmRegistered &&
      pendingAction === "add"
    ) {
      addToWishlist();
      setPendingAction(undefined);
    }
  }, [passportState, previousPassportState, pendingAction, addToWishlist]);

  return {
    gemsState: gemsState,
    isWishlisted: wishlistStatus?.subscribed ?? false,
    isWishlisting,
    addToWishlist,
    removeFromWishlist,
    mutateWishlistStatus,
  };
};
